import MessageContent, { MessageProfile } from '@sendbird/uikit-react/ui/MessageContent';
import type { MessageContentProps } from '@sendbird/uikit-react/ui/MessageContent';
import type { UserMessage } from '@sendbird/chat/message';
import { SendbirdIncomingMessageHeader } from './SendbirdIncomingMessageHeader';
import { trimMessageTextForDisplay } from '../../utils/sendbird/trimMessageTextForDisplay';
import type { SendbirdDisplayOptions } from '../../helpers/sendbirdDisplay';

const nameInitials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('');

export const initialsAvatarUrl = (name?: string | null) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="#EDEBED"/><text x="40" y="40" dy="0.35em" text-anchor="middle" font-family="General Sans, -apple-system, Helvetica, Arial, sans-serif" font-size="30" font-weight="600" fill="#22031F">${
    nameInitials(name) || '?'
  }</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const withOverride = <T extends object>(target: T, key: string, value: unknown): T => {
  const copy = Object.assign(Object.create(Object.getPrototypeOf(target)), target);
  Object.defineProperty(copy, key, { value, enumerable: true });
  return copy;
};

export const withSenderAvatar = (props: Pick<MessageContentProps, 'message' | 'channel'>): MessageContentProps['message'] => {
  const sender = props.message && 'sender' in props.message ? (props.message as UserMessage).sender : undefined;
  if (!sender?.userId) return props.message;
  const member = props.channel?.members?.find((row) => row?.userId === sender.userId);
  if (member?.profileUrl || sender.profileUrl) return props.message;
  return withOverride(props.message, 'sender', withOverride(sender, 'profileUrl', initialsAvatarUrl(sender.nickname)));
};

export const createRenderSendbirdMessageContent = (
  displayOptions?: SendbirdDisplayOptions,
  onOpenMedia?: (messageId: number) => void,
) => {
  const renderSendbirdMessageContent = (props: MessageContentProps) => (
    <MessageContent
      {...props}
      showFileViewer={
        onOpenMedia
          ? (open: boolean) => {
              if (open) onOpenMedia(props.message.messageId);
            }
          : props.showFileViewer
      }
      message={trimMessageTextForDisplay(props.message) as MessageContentProps['message']}
      renderSenderProfile={(profileProps) => (
        <MessageProfile {...profileProps} message={withSenderAvatar({ message: profileProps.message, channel: props.channel })} />
      )}
      renderMessageHeader={(headerProps) => {
        if (props.chainTop) return null;
        return (
          <SendbirdIncomingMessageHeader
            message={headerProps.message}
            currentUserId={props.userId}
            sendbirdDisplayOptions={displayOptions}
          />
        );
      }}
    />
  );

  return renderSendbirdMessageContent;
};

/** @deprecated Prefer createRenderSendbirdMessageContent when channel vendor name is known. */
export const renderSendbirdMessageContent = createRenderSendbirdMessageContent();

export default renderSendbirdMessageContent;
